import { z } from 'zod';

/**
 * `org.integrations.doubleTheDonation` request/response schemas — `GET /api-keys`,
 * `POST /api-keys`, `DELETE /api-keys`.
 *
 * Derived from the double-the-donation-integration-backend executable contract
 * (`docs/initiatives/new/backend-monorepo-migration-from-legacy-ms/contracts-legacy/
 * double-the-donation-integration-backend/schemas.ts`, re-pinned non-stale at legacy
 * `origin/dev` = `738ee7021b6d8dee7164961c4502f01ea95966eb`). All three routes are
 * served by ONE Lambda that dispatches on the HTTP method, so there is one response
 * vocabulary with per-method reachability, not three disjoint sets.
 *
 * Field names stay on the legacy wire spelling (`publicApiKey`, `privateApiKey`,
 * `nonprofit`) so the contract pins the legacy shapes bug-for-bug and the caller
 * cutover is a re-point, not a rename. Every object schema is `.strict()` — an
 * extra key on any body is a contract failure, not a passthrough.
 *
 * Envelope distinctions are preserved, NOT normalized (plan §10 landmine 8):
 * every handler-authored body except the GET 200 is a JSON-encoded STRING
 * (`"Forbidden"`, not `{"error":"Forbidden"}`) — the dashboard's
 * `DoubleTheDonationApiKeyModal.js` calls `response.json()` and assigns the
 * decoded result straight into `setErrorMessage`, which is why the string shape
 * is load-bearing. Only the GET 200 decodes to an object.
 */

// ----------------------------------------------------------------
// Lambda-envelope header maps (transport pins)
// ----------------------------------------------------------------

/**
 * The header map EVERY handler response inside `get_method` and `post_method`
 * sets, character for character (`api_keys.py:get_method`, `::post_method`).
 * Note `Access-Control-Allow-Methods: 'POST'` — it is sent on GET responses
 * too. That is not a typo here; it is the literal in the source, and a port
 * that "fixes" it is a diff.
 */
export const doubleTheDonationHandlerCorsHeadersSchema = z
  .object({
    'Access-Control-Allow-Headers': z.literal('Content-Type'),
    'Access-Control-Allow-Origin': z.literal('*'),
    'Access-Control-Allow-Methods': z.literal('POST'),
  })
  .strict();

/**
 * The DIFFERENT header map the method-fallthrough 500 sets (`api_keys.py:handler`,
 * the final return after the POST and GET branches). Two differences from
 * `doubleTheDonationHandlerCorsHeadersSchema`, both load-bearing:
 *   - `'content-type'` is LOWERCASE here, `'Content-Type'` everywhere else;
 *   - methods is `'OPTIONS,POST,GET'`, not `'POST'`.
 * A port that emits one uniform header map for all responses is a diff; this
 * schema is what makes it show up as one.
 */
export const doubleTheDonationFallthroughCorsHeadersSchema = z
  .object({
    'Access-Control-Allow-Headers': z.literal('content-type'),
    'Access-Control-Allow-Origin': z.literal('*'),
    'Access-Control-Allow-Methods': z.literal('OPTIONS,POST,GET'),
  })
  .strict();

// ----------------------------------------------------------------
// Inputs — what the dashboard sends
// ----------------------------------------------------------------

/**
 * `GET /api-keys` request. `nonprofit` is the caller's authorized merchantName.
 * Legacy's guard is `if not nonprofit:` (`api_keys.py:get_method`), so a missing
 * OR empty string takes the 400 branch — the input stays a plain string and the
 * `doubleTheDonationMissingNonprofitBodySchema` variant stays reachable.
 */
export const doubleTheDonationGetApiKeysInputSchema = z
  .object({
    nonprofit: z.string(),
  })
  .strict();
export type DoubleTheDonationGetApiKeysInput = z.infer<
  typeof doubleTheDonationGetApiKeysInputSchema
>;

/**
 * `POST /api-keys` request body: `{"publicApiKey", "privateApiKey", "nonprofit"}` —
 * all three required. Legacy reads them by bare key (`body['publicApiKey']` etc.),
 * so a missing key raises `KeyError` → gateway 500 (`GATEWAY_INTERNAL`), never a
 * handler-authored variant; at the tRPC boundary that becomes a typed transport
 * error, which is why the input schema pins all three as required strings.
 */
export const doubleTheDonationSaveApiKeysInputSchema = z
  .object({
    nonprofit: z.string(),
    publicApiKey: z.string(),
    privateApiKey: z.string(),
  })
  .strict();
export type DoubleTheDonationSaveApiKeysInput = z.infer<
  typeof doubleTheDonationSaveApiKeysInputSchema
>;

// ----------------------------------------------------------------
// Handler-authored bodies (decoded: what `response.json()` yields)
// ----------------------------------------------------------------

/**
 * `get_method` success — the ONLY variant in this repo whose decoded body is an
 * object. Both values are decrypted SSM SecureString parameters (never DynamoDB
 * passthrough), and `get_api_keys()` returns either both strings or the
 * `(None, None)` tuple from a single `except Exception` — there is no third
 * state, so the co-nullability is refined rather than left as an independent
 * pair (executable contract `ApiKeysGetBody`). The keys are opaque 360MatchPro
 * credentials and are deliberately NOT format-pinned.
 *
 * Parity note (dossier §6.8, plan §10 landmine 1): the GET path returns the
 * PRIVATE key in plaintext to any authenticated dashboard user, including
 * read-only. That is a D-010-candidate security finding to be fixed at the
 * port — this schema carries the legacy shape and does not close the gap.
 */
export const doubleTheDonationApiKeysGetSuccessSchema = z
  .object({
    publicApiKey: z.string().nullable(),
    privateApiKey: z.string().nullable(),
  })
  .strict()
  .refine((b) => (b.publicApiKey === null) === (b.privateApiKey === null), {
    message:
      'publicApiKey and privateApiKey are both-null or both-string: get_api_keys() ' +
      'returns the (None, None) tuple from a single except, or assigns both',
  });
export type DoubleTheDonationApiKeysGetSuccess = z.infer<
  typeof doubleTheDonationApiKeysGetSuccessSchema
>;

/** `get_method` 400 — query parameter absent or empty (reachable, 0 lifetime observations). */
export const doubleTheDonationMissingNonprofitBodySchema = z.literal(
  'Missing nonprofit query parameter',
);

/**
 * `get_method` / `post_method` 403 — `nonprofit not in ids` (which includes the
 * degraded org-lookup case: `ids` is `[]` whenever the V2 read misses, so an
 * org row without a merchantName presents as "Forbidden", not 404/500). On POST
 * the same return also fires when the caller is read-only — the two causes are
 * ORed into one return, and that indistinguishability is itself the contract.
 */
export const doubleTheDonationForbiddenBodySchema = z.literal('Forbidden');

/** `post_method` success, after the SSM writes and the per-form fan-out. */
export const doubleTheDonationSaveApiKeysSuccessBodySchema = z.literal('success');

/**
 * `post_method` 400 — the 360MatchPro verify call answered with
 * `public_key_valid == False`. The comparison is `== False`, so ONLY a JSON
 * `false` takes this branch; a missing key raises `KeyError` and becomes the
 * gateway 500 instead. `private_key_valid` is read by nobody: an invalid
 * PRIVATE key is stored silently (dossier §6.3).
 */
export const doubleTheDonationInvalidApiKeysBodySchema = z.literal(
  'The API keys you entered are invalid. Please try again.',
);

/**
 * `handler`'s fallthrough return — the request method is neither POST nor GET.
 * In practice this is exactly `DELETE /api-keys`: a route that IS declared and
 * deployed but whose handler branch is commented out (dossier §6.10 — a live,
 * reachable, dead route with zero lifetime calls). Its distinguishing header
 * map is `doubleTheDonationFallthroughCorsHeadersSchema`, NOT the handler map.
 */
export const doubleTheDonationMethodFallthroughBodySchema = z.literal('Something went wrong');

/**
 * `get_existing_forms`' scan-failure return — deliberately OUTSIDE every route
 * union below. That dict never reaches a caller as a response: `post_method`
 * iterates it (a dict iterates its KEYS) and `form['formID']` then raises
 * `TypeError`, so the caller sees the gateway 500 with an OBJECT body, not a
 * 500 with a string body. Modelled so the obvious "faithful" port that returns
 * this envelope to the client shows up as a diff (executable contract
 * `UNREACHABLE_GET_EXISTING_FORMS_ERROR_ENVELOPE`).
 */
export const doubleTheDonationUnreachableFormsErrorBodySchema = z.literal(
  'An error occured when getting the donor data.',
);

// ----------------------------------------------------------------
// Gateway-generated bodies (transport pins)
// ----------------------------------------------------------------

/**
 * Bodies API Gateway (HTTP API, payload 2.0) generates WITHOUT invoking the
 * handler — authorizer denials, unhandled Lambda raises, timeout/throttle 503,
 * and the unmatched-route 404 (the API declares no `$default`, so any
 * undeclared path or method falls through to it). These are TRANSPORT-level
 * legacy variants: the tRPC transport replaces them with typed errors, so they
 * are deliberately NOT members of the route output unions below. They are
 * carried because the dashboard's `setErrorMessage` fallback exists exactly
 * for them (a gateway body has `message` where the handler bodies are bare
 * strings) and the parity recorder needs the pin. Note the gateway's 403
 * `{message:'Forbidden'}` object is structurally disjoint from the handler's
 * bare-string `"Forbidden"` — the two must never be unified.
 */
export const doubleTheDonationGatewayMessageBodySchema = z.union([
  z.object({ message: z.literal('Unauthorized') }).strict(),
  z.object({ message: z.literal('Forbidden') }).strict(),
  z.object({ message: z.literal('Internal Server Error') }).strict(),
  z.object({ message: z.literal('Service Unavailable') }).strict(),
  z.object({ message: z.literal('Not Found') }).strict(),
]);

// ----------------------------------------------------------------
// Route response unions — decoded bodies, per-method reachability
// ----------------------------------------------------------------

/** `GET /api-keys` — union of every decoded body the GET branch can emit. */
export const doubleTheDonationGetApiKeysResponseSchema = z.union([
  doubleTheDonationApiKeysGetSuccessSchema,
  doubleTheDonationMissingNonprofitBodySchema,
  doubleTheDonationForbiddenBodySchema,
]);
export type DoubleTheDonationGetApiKeysResponse = z.infer<
  typeof doubleTheDonationGetApiKeysResponseSchema
>;

/** `POST /api-keys` — union of every decoded body the POST branch can emit. */
export const doubleTheDonationSaveApiKeysResponseSchema = z.union([
  doubleTheDonationSaveApiKeysSuccessBodySchema,
  doubleTheDonationInvalidApiKeysBodySchema,
  doubleTheDonationForbiddenBodySchema,
]);
export type DoubleTheDonationSaveApiKeysResponse = z.infer<
  typeof doubleTheDonationSaveApiKeysResponseSchema
>;

/**
 * `DELETE /api-keys` (and any other non-GET/POST method) — the dead-route 500.
 * One variant only: the JSON-encoded string `"Something went wrong"` over the
 * fallthrough header map. Carried bug-for-bug (D-013; plan §11 pre-declared
 * default) — correcting it to 404/405 is a port-time `x-deviations` decision,
 * not a schema normalization.
 */
export const doubleTheDonationDeleteApiKeysResponseSchema =
  doubleTheDonationMethodFallthroughBodySchema;
export type DoubleTheDonationDeleteApiKeysResponse = z.infer<
  typeof doubleTheDonationDeleteApiKeysResponseSchema
>;

/** D-032 transport additions. No request values enter these error envelopes. */
export const doubleTheDonationReplayErrorSchema = z
  .object({
    error: z.enum(['IDEMPOTENCY_KEY_REQUIRED', 'IDEMPOTENCY_CONFLICT']),
  })
  .strict();

export const doubleTheDonationHttpResultSchema = z.union([
  z
    .object({
      status: z.literal(200),
      body: z.union([
        doubleTheDonationApiKeysGetSuccessSchema,
        doubleTheDonationSaveApiKeysSuccessBodySchema,
      ]),
    })
    .strict(),
  z
    .object({
      status: z.literal(400),
      body: z.union([
        doubleTheDonationMissingNonprofitBodySchema,
        doubleTheDonationInvalidApiKeysBodySchema,
        z.object({ error: z.literal('IDEMPOTENCY_KEY_REQUIRED') }).strict(),
      ]),
    })
    .strict(),
  z.object({ status: z.literal(403), body: doubleTheDonationForbiddenBodySchema }).strict(),
  z
    .object({
      status: z.literal(409),
      body: z.object({ error: z.literal('IDEMPOTENCY_CONFLICT') }).strict(),
    })
    .strict(),
  z.object({ status: z.literal(500), body: doubleTheDonationMethodFallthroughBodySchema }).strict(),
]);
